import React, { useState, useEffect } from 'react';
import {
  Stack,
  Group,
  Text,
  Card,
  Badge,
  Box,
  Title,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { IconStar, IconStarFilled, IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  IJournalCategory,
  IJournalQuestion,
  IJournalResponseInput,
  IResponseMessage,
  IPatientPriorityQuestion,
  QuestionOptions,
  INumericScaleOptions,
} from '../../../global/entities/Journal/Journal';
import {
  SimpleYesNoQuestion,
  YesNoScaleQuestion,
  YesNoNumericScaleQuestion,
  YesNoCounterQuestion,
  ConditionalMultiselectQuestion,
  MultipleSelectionQuestion,
  FreetextQuestion,
  ScaleQuestion,
  ToggleQuestion,
} from './questions';
import { QUESTION_TYPE_IDS } from './QuestionTypes';
import { ResponseMessage } from './questions/ResponseMessage';

interface JournalCategoryStepProps {
  category: IJournalCategory;
  responses: IJournalResponseInput[];
  onResponsesChange: (responses: IJournalResponseInput[]) => void;
  onValidationChange?: (
    hasValidationErrors: boolean,
    errorQuestionIds?: number[],
  ) => void;
  readOnly?: boolean;
  priorityMap?: Map<number, IPatientPriorityQuestion>;
  onTogglePriority?: (questionId: number, isPriority: boolean) => void;
}

export const JournalCategoryStep: React.FC<JournalCategoryStepProps> = ({
  category,
  responses,
  onResponsesChange,
  onValidationChange,
  readOnly = false,
  priorityMap,
  onTogglePriority,
}) => {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language as 'en' | 'fr';

  // Track validation errors for questions in this category
  const [validationErrors, setValidationErrors] = useState<Set<number>>(
    new Set(),
  );

  // Report validation state changes to parent
  useEffect(() => {
    const hasErrors = validationErrors.size > 0;
    const errorQuestionIds = Array.from(validationErrors);
    onValidationChange?.(hasErrors, errorQuestionIds);
  }, [validationErrors, onValidationChange]);

  // Update response for a specific question
  const updateResponse = (
    questionId: number,
    updates: Partial<IJournalResponseInput>,
  ) => {
    const newResponses = responses.map((response) =>
      response.journalQuestionID === questionId
        ? { ...response, ...updates, responseDate: new Date().toISOString() }
        : response,
    );
    onResponsesChange(newResponses);
  };

  // Handle validation error changes for questions
  const handleValidationChange = (questionId: number, hasError: boolean) => {
    setValidationErrors((prev) => {
      const newErrors = new Set(prev);
      if (hasError) newErrors.add(questionId);
      else newErrors.delete(questionId);

      return newErrors;
    });
  };

  // Get response for a specific question
  const getResponse = (
    questionId: number,
  ): IJournalResponseInput | undefined => {
    return responses.find((r) => r.journalQuestionID === questionId);
  };

  // Render question based on its type
  const renderQuestion = (question: IJournalQuestion) => {
    const response = getResponse(question.id);
    const questionText =
      currentLang === 'fr' ? question.questionTextFr : question.questionTextEn;
    const description =
      currentLang === 'fr' ? question.descriptionFr : question.descriptionEn;

    // Parse options JSON if exists
    let options: QuestionOptions | null = null;
    try {
      if (question.optionsJson) options = JSON.parse(question.optionsJson);
    } catch (e) {
      console.warn('Failed to parse question options:', e);
    }

    const priorityInfo = priorityMap?.get(question.id);
    const isEffectivePriority = priorityInfo?.isPriority ?? false;
    const isLocked = priorityInfo?.isPriorityLocked ?? false;

    return (
      <Card
        key={question.id}
        withBorder
        p="md"
        mb="md"
        data-question-id={question.id}
      >
        <Stack gap="sm">
          {/* Question header */}
          <div>
            <Group justify="space-between" align="flex-start">
              <Text fw={500} size="md" mb="xs" style={{ flex: 1 }}>
                {questionText}
              </Text>
              {priorityMap &&
                onTogglePriority &&
                (isLocked ? (
                  <Tooltip label={t('journal.priorityLocked')}>
                    <Badge
                      size="sm"
                      variant="light"
                      color="yellow"
                      leftSection={<IconLock size={10} />}
                    >
                      <IconStarFilled size={10} />
                    </Badge>
                  </Tooltip>
                ) : (
                  <ActionIcon
                    color={isEffectivePriority ? 'yellow' : 'gray'}
                    variant="subtle"
                    size="sm"
                    title={
                      isEffectivePriority
                        ? t('journal.removeFromPriority')
                        : t('journal.markAsPriority')
                    }
                    onClick={() =>
                      onTogglePriority(question.id, !isEffectivePriority)
                    }
                  >
                    {isEffectivePriority ? (
                      <IconStarFilled size={16} />
                    ) : (
                      <IconStar size={16} />
                    )}
                  </ActionIcon>
                ))}
            </Group>
            {description && (
              <Text size="sm" c="dimmed" mb="sm">
                {description}
              </Text>
            )}
          </div>

          {/* Question input based on type */}
          <Box>{renderQuestionInput(question, response, options)}</Box>

          {/* Conditional response messages */}
          {options && (options as any).responseMessages && response && (
            <ResponseMessage
              messages={(options as any).responseMessages as IResponseMessage[]}
              responseValue={response.responseValue}
              subResponseValue={response.subResponseValue}
              currentLang={currentLang}
            />
          )}
        </Stack>
      </Card>
    );
  };

  // Render input based on question type and options
  const renderQuestionInput = (
    question: IJournalQuestion,
    response: IJournalResponseInput | undefined,
    options: QuestionOptions | null,
  ) => {
    // Determine question type from database ID or options
    if (!options && question.questionTypeID === QUESTION_TYPE_IDS.FREE_TEXT) {
      // Default to free text if no options specified
      return (
        <FreetextQuestion
          questionId={question.id}
          response={response}
          onResponseChange={updateResponse}
          readOnly={readOnly}
        />
      );
    }

    switch (question.questionTypeID) {
      case QUESTION_TYPE_IDS.YES_NO:
        return (
          <SimpleYesNoQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            readOnly={readOnly}
          />
        );

      case QUESTION_TYPE_IDS.YES_NO_WITH_SCALE:
        return (
          <YesNoScaleQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            readOnly={readOnly}
            isReversed={question.isReversed}
          />
        );

      case QUESTION_TYPE_IDS.YES_NO_WITH_NUMERIC_SCALE: {
        const numericScaleOptions = options as INumericScaleOptions | null;
        return (
          <YesNoNumericScaleQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            readOnly={readOnly}
            min={numericScaleOptions?.min}
            max={numericScaleOptions?.max}
            step={numericScaleOptions?.step}
            labelMin={numericScaleOptions?.labelMin}
            labelMax={numericScaleOptions?.labelMax}
          />
        );
      }

      case QUESTION_TYPE_IDS.YES_NO_WITH_COUNT:
        return (
          <YesNoCounterQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            onValidationChange={handleValidationChange}
            min={options ? (options as any).min || 0 : 0}
            max={options ? (options as any).max || 999 : 999}
            unit={
              options && currentLang === 'fr'
                ? (options as any).unit?.fr
                : options && currentLang === 'en'
                  ? (options as any).unit?.en
                  : ''
            }
            placeholder={
              options && currentLang === 'fr'
                ? (options as any).placeholder?.fr
                : options && currentLang === 'en'
                  ? (options as any).placeholder?.en
                  : ''
            }
            readOnly={readOnly}
          />
        );

      case QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT:
        if (!options) return null;
        return (
          <ConditionalMultiselectQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            options={options}
            currentLang={currentLang}
            readOnly={readOnly}
          />
        );

      case QUESTION_TYPE_IDS.MULTI_SELECT:
        if (!options) return null;
        return (
          <MultipleSelectionQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            options={options}
            currentLang={currentLang}
            readOnly={readOnly}
          />
        );

      case QUESTION_TYPE_IDS.SCALE:
        return (
          <ScaleQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            readOnly={readOnly}
            isReversed={question.isReversed}
          />
        );

      case QUESTION_TYPE_IDS.FREE_TEXT:
        return (
          <FreetextQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            readOnly={readOnly}
          />
        );

      case QUESTION_TYPE_IDS.TOGGLE:
        return (
          <ToggleQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            options={options}
            currentLang={currentLang}
            readOnly={readOnly}
          />
        );

      default:
        return (
          <FreetextQuestion
            questionId={question.id}
            response={response}
            onResponseChange={updateResponse}
            readOnly={readOnly}
          />
        );
    }
  };

  return (
    <Card withBorder p="lg">
      <Stack gap="lg">
        {/* Category header */}
        <Box>
          <Group justify="space-between" mb="sm">
            <Title order={4}>
              {currentLang === 'fr' ? category.nameFr : category.nameEn}
            </Title>
            <Badge variant="light" color="blue">
              {category.questions.length} {t('journal.questions')}
            </Badge>
          </Group>
        </Box>

        {/* Questions */}
        <Stack gap="md">
          {(category.id === -1
            ? category.questions
            : [...category.questions].sort((a, b) => a.order - b.order)
          ).map((question) => renderQuestion(question))}
        </Stack>
      </Stack>
    </Card>
  );
};
